/* eslint-disable react/jsx-no-useless-fragment */
import { useAppConfigContext } from '@/context/AppConfig';
import { ReportSection } from '@/reports/index';
import { StyledEditIcon } from '@/reports/shared/OverviewItems/components';
import { defaultValueForRoomCounts } from '@/reports/shared/OverviewItems/utils';
import { serializedTypes } from '@/reports/types';
import { Building } from '@/reports/types/properties';
import { Text, Table, Card, ColumnValue, styled, SxProps } from '@/theme/index';
import {
  getOverallAttributesDetails,
  getRoomsCountDetails,
} from '../../utils/lotAndBuildings';

const StyledTable = styled(Table)(() => ({
  '.MuiTableRow-root td:nth-of-type(3)': {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  'tbody > .MuiTableRow-root': {
    '.MuiTableCell-body': {
      fontSize: '13px',
    },
  },
}));

const TableAdjusted = styled(Table)(() => ({
  'tbody > .MuiTableRow-root': {
    '.MuiTableCell-body': {
      fontSize: '13px',
    },
  },
}));

export interface LotAndBuildingDetailsProps {
  propertyAddress: string;
  property: serializedTypes.SerializedProperty;
  canEditProperty?: boolean;
  updatedBuilding?: Building;
  setIsEditModalOpen?: () => void;
  permalink?: string;
}

export function LotAndBuildingDetails({
  propertyAddress,
  property,
  canEditProperty,
  updatedBuilding,
  setIsEditModalOpen,
  permalink,
}: LotAndBuildingDetailsProps) {
  const { trackEvent } = useAppConfigContext();
  const lotAndbuildingDetails = property?.lotAndBuildingDetails;
  const hasOverallAttributes =
    lotAndbuildingDetails?.overallAttributes?.hasData;
  const hasInteriorDimensions =
    lotAndbuildingDetails?.interiorDimensions?.hasData;
  const hasRoomCounts = lotAndbuildingDetails?.roomCounts?.hasData;

  const hasBuildingDetails = Boolean(
    hasOverallAttributes || hasInteriorDimensions || hasRoomCounts
  );
  const hasLotDetails = Boolean(lotAndbuildingDetails?.lotDetails?.hasData);

  const handleOpen = () => {
    trackEvent(
      'edit property details entry point',
      'engagement click',
      'lot and building details section'
    );
    if (setIsEditModalOpen) {
      setIsEditModalOpen();
    }
  };

  const dynamicEditButton = (sx?: SxProps) => {
    if (!canEditProperty) return <></>; // ColumnValue type expects a JSX.Element, so we need to return a fragment

    return (
      <StyledEditIcon
        sx={{
          color: (theme) => theme.palette.text.primary,
          ...sx,
        }}
        onClick={handleOpen}
        data-testid="edit-button"
      />
    );
  };

  const createCustomRoomsRows = (building: Building): ColumnValue[][] => {
    const roomDetails = getRoomsCountDetails(building);
    return [
      [
        'Total room count',
        String(
          defaultValueForRoomCounts(
            (updatedBuilding?.rooms?.bed || Number(roomDetails.bedrooms)) +
              (updatedBuilding?.rooms?.baths?.total ||
                Number(roomDetails.bathrooms)) || roomDetails.roomsCount
          )
        ),
        <></>,
      ],
      [
        'Bedrooms',
        String(
          defaultValueForRoomCounts(
            updatedBuilding?.rooms?.bed || roomDetails.bedrooms
          )
        ),

        dynamicEditButton(),
      ],
      [
        'Bathrooms',
        String(
          defaultValueForRoomCounts(
            updatedBuilding?.rooms?.baths?.total || roomDetails.bathrooms
          )
        ),
        dynamicEditButton(),
      ],
      ['Basement', roomDetails.buildingBathBasement, <></>],
    ];
  };

  const createCustomSqftRows = (building: Building): ColumnValue[][] => {
    const overallAttributes = getOverallAttributesDetails(building);
    return [
      [
        'Total square footage',
        String(updatedBuilding?.area || overallAttributes.overallBuildingArea),
        dynamicEditButton(),
      ],
      ['Number of stories', overallAttributes.buildingStories, <></>],
      ['Number of units', overallAttributes.buildingUnits, <></>],
      ['Year built', overallAttributes.yearBuilt, <></>],
      ['Style', overallAttributes.buildingStyle, <></>],
      ['Class', overallAttributes.buildingClass, <></>],
      ['Construction type', overallAttributes.constructionType, <></>],
    ];
  };

  const updatedRoomCountsRows = canEditProperty
    ? createCustomRoomsRows(property?.buildings?.[0])
    : lotAndbuildingDetails?.roomCounts?.rows;

  const updatedOverallAttributesRows = canEditProperty
    ? createCustomSqftRows(property?.buildings?.[0])
    : lotAndbuildingDetails?.overallAttributes?.rows;

  return (
    <ReportSection
      title="Lot & Building Details"
      sectionDescription="Electronically accessible details on the lot and building(s) that comprise this property are shown below."
      sectionId="lot-and-building-details-section"
      hasContent={hasBuildingDetails || hasLotDetails}
      propertyAddress={propertyAddress}
      permalink={permalink}
    >
      {hasBuildingDetails && !property.firstBuilding?.rooms?.hidden && (
        <Card variant="outlined" sx={{ mb: 2.5, padding: 2.5 }}>
          <Text variant="h2" sx={{ mb: 3.2 }}>
            Building Details
          </Text>
          <Text variant="h3">Overall Attributes</Text>
          <StyledTable
            className="striped flipped"
            rows={updatedOverallAttributesRows}
          />
          <Text variant="h3" sx={{ mt: 2.5, mb: 1.5 }}>
            Interior Dimensions
          </Text>
          <TableAdjusted
            className="striped flipped"
            rows={lotAndbuildingDetails?.interiorDimensions?.rows}
          />
          <Text variant="h3" sx={{ mt: 2.5, mb: 1.5 }}>
            Room Counts
          </Text>
          <StyledTable
            className="striped flipped"
            rows={updatedRoomCountsRows}
          />
        </Card>
      )}
      {hasLotDetails && !property.firstBuilding?.codes?.hidden && (
        <Card variant="outlined" sx={{ mb: 2.5, padding: 2.5 }}>
          <Text variant="h2">Lot Details</Text>
          <TableAdjusted
            className="striped flipped"
            rows={lotAndbuildingDetails?.lotDetails?.rows}
          />
        </Card>
      )}
    </ReportSection>
  );
}
